<template>
    <section class="carousel" :aria-label="label" aria-roledescription="carousel">
        <div class="viewport" :class="{ 'is-sliding': animated, 'is-fitted': fitActive }">
            <div
                v-for="(id, index) in itemIds"
                :key="id"
                class="slide"
                :class="{ 'is-before': index < currentIndex, 'is-after': index > currentIndex }"
                role="group"
                aria-roledescription="slide"
                :aria-label="`${index + 1} of ${itemIds.length}`"
                :aria-hidden="id !== activeId"
                :inert="id !== activeId">
                <slot :name="`item-${id}`"></slot>
            </div>
        </div>
        <div v-if="!hideControls && itemIds.length > 1" class="controls">
            <BaseButton
                theme="tertiary"
                size="sm"
                icon-only
                data-test-id="carousel-prev"
                class="control"
                :disabled="currentIndex === 0"
                :aria-label="`Previous ${itemLabel}`"
                @click="move(-1)"
                @keydown="handleControlKey">
                <i class="bi bi-arrow-left" aria-hidden="true"></i>
            </BaseButton>
            <div class="progress">
                <output v-if="showCount" class="count" :aria-label="`Current ${itemLabel}`">{{ currentIndex + 1 }} of {{ itemIds.length }}</output>
                <div class="dots">
                    <BaseButton
                        v-for="(id, index) in itemIds"
                        :key="id"
                        theme="tertiary"
                        size="sm"
                        icon-only
                        class="dot-button"
                        :aria-label="`Go to ${itemLabel} ${index + 1}`"
                        :aria-current="activeId === id ? 'true' : undefined"
                        @click="activeId = id"
                        @keydown="handleControlKey">
                        <span class="dot" :class="{ 'is-active': activeId === id }" aria-hidden="true"></span>
                    </BaseButton>
                </div>
            </div>
            <BaseButton
                theme="tertiary"
                size="sm"
                icon-only
                data-test-id="carousel-next"
                class="control"
                :disabled="currentIndex === itemIds.length - 1"
                :aria-label="`Next ${itemLabel}`"
                @click="move(1)"
                @keydown="handleControlKey">
                <i class="bi bi-arrow-right" aria-hidden="true"></i>
            </BaseButton>
        </div>
    </section>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import { invariant } from "~vue/utils";
import { computed, watch } from "vue";

const {
    itemIds,
    hideControls = false,
    animated = false,
    showCount = false,
    fitActive = false,
    label = "Carousel",
    itemLabel = "item",
} = defineProps<{
    itemIds: string[];
    hideControls?: boolean;
    animated?: boolean;
    showCount?: boolean;
    /**
     * Fits the active slide's height. Disabled by default to prevent content below the carousel from
     * shifting; enable to avoid excess space beneath shorter slides.
     */
    fitActive?: boolean;
    label?: string;
    itemLabel?: string;
}>();

const activeId = defineModel<string>({ default: "" });
const currentIndex = computed(() => itemIds.indexOf(activeId.value));
watch(
    () => itemIds,
    (ids) => {
        invariant(ids.length > 0, "BaseCarousel requires at least one itemId");
        if (!ids.includes(activeId.value)) {
            activeId.value = ids[0];
        }
    },
    { immediate: true },
);

function handleControlKey(event: KeyboardEvent): void {
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        move(event.key === "ArrowLeft" ? -1 : 1);
    }
}

function move(delta: number): void {
    const next = currentIndex.value + delta;
    if (next >= 0 && next < itemIds.length) {
        activeId.value = itemIds[next];
    }
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .carousel {
        @apply flex min-w-0 flex-col justify-between gap-4;
    }

    .viewport {
        @apply relative grid min-w-0;
    }

    .viewport.is-sliding {
        @apply overflow-hidden;
    }

    .slide {
        @apply col-start-1 row-start-1 min-w-0;
    }

    .slide[aria-hidden="true"] {
        @apply invisible pointer-events-none;
    }

    /*
     * Keep inactive slides positioned over the viewport so outgoing animations remain visible without
     * affecting the active slide's height.
     */
    .is-fitted .slide[aria-hidden="true"] {
        @apply absolute inset-0;
    }

    .is-sliding .slide {
        @apply transition-[transform,opacity] duration-200 ease-out motion-reduce:transition-none;
    }

    .is-sliding .slide[aria-hidden="true"] {
        @apply visible opacity-0;
    }

    .is-sliding .slide.is-before {
        @apply -translate-x-full;
    }

    .is-sliding .slide.is-after {
        @apply translate-x-full;
    }

    .controls {
        @apply flex w-full items-center justify-between gap-2;
    }

    .control {
        @apply flex size-8 items-center justify-center rounded-full bg-carousel-control-bg text-carousel-control-text transition-colors hover:bg-carousel-control-hover-bg disabled:bg-carousel-control-disabled-bg disabled:opacity-50;
    }

    .progress {
        @apply flex flex-col items-center;
    }

    .count {
        @apply scaffold-details-small text-ink-muted;
    }

    .dots {
        @apply flex;
    }

    .dot-button {
        @apply flex size-8 items-center justify-center rounded-full;
    }

    .dot {
        @apply size-2.5 rounded-full border border-carousel-dot-border;
    }

    .dot.is-active {
        @apply bg-carousel-dot-active-bg;
    }
}
</style>
